import React, { useState } from 'react';
import { ChevronLeft, Check, X, AlertTriangle, Loader } from 'lucide-react';
import { useFormContext } from '../context/FormContext';
import { useAuth } from '../context/AuthContext';

const formatMultiSelectionValue = (value) => {
    if (!value) return '未填写';
    
    if (Array.isArray(value)) {
      if (value.length === 0) return '未填写';
      
      return (
        <div className="space-y-2.5">
          {value.map((item, idx) => {
            // Extract the option letter if it exists (e.g., "A. 立刻写下任务...")
            const match = item.match(/^([A-Z]\.)\s(.+)$/);
            
            if (match) {
              // If it has a letter prefix, format it nicely
              return (
                <div key={idx} className="flex items-start">
                  <span className="text-blue-600 font-medium min-w-[24px]">{match[1]}</span>
                  <span className="text-gray-800">{match[2]}</span>
                </div>
              );
            } else {
              // Otherwise use a bullet point
              return (
                <div key={idx} className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  <span className="text-gray-800">{item}</span>
                </div>
              );
            }
          })}
        </div>
      );
    }
    
    return value;
  };

  // Helper function to decode HTML entities
const decodeHtmlEntities = (text) => {
    if (!text) return '';
    
    const entities = {
      '&quot;': '"',
      '&amp;': '&',
      '&lt;': '<',
      '&gt;': '>',
      '&apos;': "'",
      '&#39;': "'",
      '&#x2F;': '/',
      '&nbsp;': ' '
    };
    
    return text.replace(/&quot;|&amp;|&lt;|&gt;|&apos;|&#39;|&#x2F;|&nbsp;/g, match => entities[match]);
  };
  
// Helper function to check if a value is completed
const isCompleted = (value) => {
  if (Array.isArray(value)) {
    return value && value.length > 0;
  }
  return value && value !== '';
};

const ReviewPage = ({ onSubmit, onBack, onEditQuestion, categories }) => {
  const { formData, assessmentId } = useFormContext();
  const { currentUser } = useAuth();
  const [showIncompleteModal, setShowIncompleteModal] = useState(false);
  const [missingQuestions, setMissingQuestions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Count completed questions per category for progress indicators
  const calculateCompletionByCategory = () => {
    return [
      // Category 0 completion (Personal Info)
      {
        total: 6,
        completed: [
          isCompleted(formData.fullName),
          isCompleted(formData.wechat),
          isCompleted(formData.email),
          isCompleted(formData.school),
          isCompleted(formData.grade),
          isCompleted(formData.major)
        ].filter(Boolean).length
      },
      // Category 1 completion (Background)
      {
        total: 8,
        completed: [
          isCompleted(formData.bestCourses),
          isCompleted(formData.topSkills),
          isCompleted(formData.weakness),
          isCompleted(formData.weaknessExamples),
          isCompleted(formData.englishLevel),
          isCompleted(formData.englishStoryDifference),
          isCompleted(formData.needVisa),
          isCompleted(formData.visaType)
        ].filter(Boolean).length
      },
      // Category 2 completion (Industry)
      {
        total: 4,
        completed: [
          isCompleted(formData.industryInterests),
          isCompleted(formData.industryInterestsReason),
          isCompleted(formData.notInterestedIndustries),
          isCompleted(formData.careerCertainty)
        ].filter(Boolean).length
      },
      // Category 3 completion (Learning)
      {
        total: 3,
        completed: [
          isCompleted(formData.interestingTopics),
          isCompleted(formData.flowExperience),
          isCompleted(formData.assignmentPreference),
        ].filter(Boolean).length
      },
      // Category 4 completion (Environment)
      {
        total: 4,
        completed: [
          isCompleted(formData.collaborationStyle),
          isCompleted(formData.motivation),
          isCompleted(formData.feedbackPreference),
          isCompleted(formData.groupRole),
        ].filter(Boolean).length
      },
      // Category 5 completion (Social)
      {
        total: 4,
        completed: [
          isCompleted(formData.conflictResolution),
          isCompleted(formData.networkingStyle),
          isCompleted(formData.deadlineResponses),
          isCompleted(formData.workQualityUnderPressure)
        ].filter(Boolean).length
      },
      // Category 6 completion (Decision)
      {
        total: 6,
        completed: [
          isCompleted(formData.decisionMakingStyle),
          isCompleted(formData.attentionToDetail),
          isCompleted(formData.personDescription),
          isCompleted(formData.vacationPlanning),
          isCompleted(formData.riskPreference),
          isCompleted(formData.riskType)
        ].filter(Boolean).length
      },
      // Category 7 completion (Achievement)
      {
        total: 3,
        completed: [
          isCompleted(formData.achievementExperiences),
          isCompleted(formData.successCriteria),
          isCompleted(formData.jobFactors)
        ].filter(Boolean).length
      },
      // Category 8 completion (Adaptability)
      {
        total: 4,
        completed: [
          isCompleted(formData.adaptationTime),
          isCompleted(formData.failureResponse),
          isCompleted(formData.criticismHandling),
          isCompleted(formData.ambiguityComfort)
        ].filter(Boolean).length
      }
    ];
  };
  
  const categoryCompletion = calculateCompletionByCategory();
  
  // Group questions by categories
  const categoryQuestions = [
    // First category: 个人信息
    [
        {
        label: "姓名",
        value: formData.fullName,
        question: "你的姓名（或昵称）"
        },
        {
        label: "微信",
        value: formData.wechat,
        question: "你的微信号"
        },
        {
        label: "邮箱",
        value: formData.email,
        question: "你的邮箱"
        },
        {
        label: "学校",
        value: formData.school,
        question: "你的学校"
        },
        {
        label: "年级",
        value: formData.grade,
        question: "你的年级"
        },
        {
        label: "专业",
        value: formData.major,
        question: "你的专业（备注是否打算转专业）"
        }
    ],
    // Second category: 能力背景及身份状态
    [
      { label: "最擅长的课程", value: formData.bestCourses, question: "你认为自己最擅长的2-3门课程是什么?" },
      { label: "最擅长的能力或特质", value: formData.topSkills, question: "你认为自己最擅长的2-3个能力或个人特质是什么?" },
      { label: "他人常提醒的问题", value: formData.weakness, question: "有没有哪一点或几点，是别人比较常提醒/吐槽你的?" },
      { label: "问题的具体例子", value: formData.weaknessExamples, question: "如果有的话，可以给每个点举个例子吗?" },
      {
        label: "英文表达能力",
        value: formData.englishLevel === formData.englishLevelOther ? formData.englishLevelOther : formData.englishLevel,
        question: "请描述你的英文程度"
      },
      { label: "英文表达差距", value: formData.englishStoryDifference, question: "如果中文表达的领导力故事和英文表达的版本有差距，差在哪?" },
      { label: "需要工作签证", value: formData.needVisa, question: "你未来在美国找工作时是否需要雇主提供工作签证?" },
      {
        label: "签证类别",
        value: formData.visaType === formData.visaTypeOther ? formData.visaTypeOther : formData.visaType,
        question: "你的签证类别是哪种?"
      }
    ],
    // Third category: 行业兴趣
    [
      { 
        label: "感兴趣的行业", 
        value: formData.industryInterests,
        question: "下面这些领域中，哪几个你觉得有点有趣、想要探索了解更多？" 
      },
      {
        label: "选择原因",
        value: formData.industryInterestsReason,
        question: "你为什么选择了上面那些觉得有趣/想了解的领域？"
      },
      {
        label: "不感兴趣的行业",
        value: formData.notInterestedIndustries,
        question: "有没有哪些领域你&quot;肯定不想做&quot;或&quot;完全不感兴趣&quot;？"
      },
      {
        label: "职业方向确定程度",
        value: formData.careerCertainty,
        question: "你觉得自己对未来想做的领域有多确定？"
      }
    ],
    // Fourth category: 学习内容兴趣模式
    [
      {
        label: "自发学习内容",
        value: formData.interestingTopics,
        question: "过去一学期/或者最近一个月，有什么主题/内容，会让你自发地学习探索 ，愿意主动查资料，或者平时浏览网页时主动点击进去了解？"
      },
      {
        label: "沉浸体验",
        value: formData.flowExperience,
        question: "回想一下最近一次你完全投入在某件事中以至于忘记了时间的经历。那时候在做什么？通常什么类型的事情会让你这样投入?"
      },
      {
        label: "作业类型偏好",
        value: formData.assignmentPreference,
        question: "你更喜欢&quot;如何改善这个产品&quot;（开放式）还是&quot;计算这个公司的估值&quot;（结构化）类型的作业? 为什么这个选择对你更有吸引力?"
      },
    ],
    // Fifth category: 学习环境偏好
    [
      {
        label: "协作风格",
        value: formData.collaborationStyle,
        question: "当你刚接到一个复杂项目或任务时，你更自然的反应是？"
      },
      {
        label: "工作方式",
        value: formData.motivation,
        question: "哪种工作方式更能让你产生动力、投入感？"
      },
      {
        label: "反馈偏好",
        value: formData.feedbackPreference,
        question: "你提交了一份报告初稿，教授提供了两种不同类型的反馈：一位教授详细标出了需要修改的具体段落和建议的修改方向，另一位只提供了一些大方向的思考问题让你自己探索改进。哪种反馈方式你更喜欢？为什么?"
      },
      {
        label: "小组角色倾向",
        value: formData.groupRole,
        question: "回想过去的小组工作经历（如有）。在没有明确分工的小组项目中，你通常会自然而然地承担什么角色？"
      },
    ],
    // Sixth category: 社交行为模式
    [
      {
        label: "冲突解决方式",
        value: formData.conflictResolution === formData.conflictResolutionOther ? 
                formData.conflictResolutionOther : formData.conflictResolution,
        question: "小组讨论中，关于项目方向出现了严重分歧，气氛变得紧张。各方坚持不同的方案。你通常会如何应对这种情况？"
      },
      {
        label: "社交策略",
        value: formData.networkingStyle,
        question: "回忆一次进入新环境（如新班级或学生组织）的经历。你如何建立新的联系？你是主动接触特定人群，还是让关系自然发展？描述你在头两周内你会采取的社交策略。"
      },
      {
        label: "截止日期应对",
        value: formData.deadlineResponses,
        question: "当面临多个紧急截止日期时，时间明显不够用，你的典型反应是什么？"
      },
      {
        label: "压力下表现",
        value: formData.workQualityUnderPressure,
        question: "在上述这种压力情况下，你的工作质量相比平时通常:"
      }
    ],
    // Seventh category: 决策模式
    [
        {
        label: "信息与直觉",
        value: formData.decisionMakingStyle,
        question: "在没有完整信息的情况下，你更倾向于依靠你的直觉进行判断，还是尽可能收集更多数据后才做决定?"
        },
        {
        label: "细节关注度",
        value: formData.attentionToDetail,
        question: "你是一个注重细节的人吗，还是觉得大方向对就可以了，对于抠细节没那么有耐心?"
        },
        {
        label: "人物描述",
        value: formData.personDescription,
        question: "想象你今天刚认识了一个新朋友，朋友或者你爸妈问你：&quot;这个人怎么样？&quot;你会怎么回答？请写出你会说的话。"
        },
        {
        label: "旅行计划风格",
        value: formData.vacationPlanning,
        question: "回想你上一次安排假期出游：你是提前订好酒店、交通、景点票、每天路线都写好的人，还是只订机票/酒店，到时候再决定每天去哪玩?"
        },
        {
        label: "风险偏好",
        value: formData.riskPreference,
        question: "如果你申请大学的时候只能选 5 所学校，你觉得你更可能怎么分配？"
        },
        {
        label: "风险类型",
        value: formData.riskType,
        question: "你觉得你更像哪种类型的人？"
        }
    ],
    // Eighth category: 成就与满足感来源
    [
        {
        label: "成就感经历特征",
        value: formData.achievementExperiences,
        question: "回顾你过去一年最有成就感的几件事。这些经历有什么共同特征? 什么元素让你感到特别有满足感？"
        },
        {
        label: "成功项目标准",
        value: formData.successCriteria,
        question: "想象你负责一个完全按照自己想法来做的项目。什么样的成果会让你觉得&quot;这真是个成功的项目&quot;？"
        },
        {
        label: "理想工作因素",
        value: formData.jobFactors,
        question: "在选择理想工作时，哪些因素对你来说最重要？"
        }
    ],
    // Ninth category: 适应力指标
    [
        {
        label: "新环境适应时间",
        value: formData.adaptationTime,
        question: "当进入全新环境（如新学校、新团队）时，你通常多久适应，即感到舒适并有效运作？"
        },
        {
        label: "失败后恢复方式",
        value: formData.failureResponse,
        question: "当你的努力没有达到预期结果时（如考试成绩不理想、项目受到批评），你通常如何反应和恢复？"
        },
        {
        label: "处理批评方式",
        value: formData.criticismHandling,
        question: "当收到关于你工作的建设性批评时，你通常如何处理这些信息？"
        },
        {
        label: "对模糊情境的适应性",
        value: formData.ambiguityComfort,
        question: "当你被分配到一个任务，没有明确指示、没有明确标准可循，你的感觉如何？这会让你感到刺激兴奋还是不安？"
        }
    ]
  ];

  // Function to check if a question is answered
  const isQuestionAnswered = (value) => {
    if (Array.isArray(value)) {
      return value && value.length > 0;
    }
    return value && value !== '';
  };

  // Check if all questions are answered
  const checkAllQuestionsAnswered = () => {
    const incomplete = [];
    
    categoryQuestions.forEach((category, categoryIndex) => {
      category.forEach((question, questionIndex) => {
        if (!isQuestionAnswered(question.value)) {
          incomplete.push({
            categoryIndex,
            questionIndex,
            categoryName: categories[categoryIndex].name,
            questionLabel: question.label
          });
        }
      });
    });
    
    return { complete: incomplete.length === 0, incomplete };
  };
  
  // Handle submit button click
  const handleSubmit = async () => {
    const { complete, incomplete } = checkAllQuestionsAnswered();
    
    if (complete) {
      // If all questions are answered, proceed with submission
      try {
        setIsSubmitting(true);
        // Call the parent onSubmit handler which calls completeAssessment
        await onSubmit();
      } catch (error) {
        console.error("Error submitting assessment:", error);
        // Could add error modal here
        setIsSubmitting(false);
      }
    } else {
      // If some questions are not answered, show the modal
      setMissingQuestions(incomplete);
      setShowIncompleteModal(true);
    }
  };
  
  // Function to navigate to the first unanswered question
  const goToFirstIncompleteQuestion = () => {
    if (missingQuestions.length > 0) {
      const firstMissing = missingQuestions[0];
      
      // Calculate the overall index for this question
      let overallIndex = firstMissing.questionIndex;
      for (let i = 0; i < firstMissing.categoryIndex; i++) {
        overallIndex += categoryQuestions[i].length;
      }
      
      // Navigate to that question
      onEditQuestion(overallIndex);
      setShowIncompleteModal(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-10 max-w-4xl mx-auto">
      {/* Logo at the top of review page */}
      <div className="flex justify-center mb-6">
        <div className="flex items-center">
          <img src="new-logo-blue.png" alt="Careers Compass" className="h-7 object-contain" />
        </div>
      </div>
      
      <div className="max-w-2xl mx-auto mb-8">
        <h2 className="text-3xl font-bold mb-4 text-center bg-gradient-to-r from-blue-600 to-indigo-700 inline-block text-transparent bg-clip-text w-full">评估问卷回顾</h2>
        <div className="h-1 w-24 bg-gradient-to-r from-blue-400 to-indigo-500 mx-auto mb-6 rounded-full"></div>
        <p className="text-gray-600 text-center text-lg">请检查您的回答。点击任意问题可直接跳转至该问题进行编辑。</p>
      </div>
      
      {/* Category completion summary */}
      <div className="mb-10 max-w-2xl mx-auto">
        <div className="bg-gray-50 rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-700 mb-4 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
              <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
              <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
            </svg>
            问卷完成度
          </h3>
          
          <div className="flex flex-col space-y-4">
            {categories.map((category, idx) => (
              <div key={idx} className="flex flex-col">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center">
                    <span className="text-lg mr-2">{category.icon}</span>
                    <span className="text-gray-700 font-medium">{category.name}</span>
                  </div>
                  <span className="text-sm text-gray-500">
                    {categoryCompletion[idx].completed} / {categoryCompletion[idx].total} 已完成
                  </span>
                </div>
                <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden">
                  <div 
                    className={`h-full transition-all duration-500 ease-out ${
                      categoryCompletion[idx].completed === categoryCompletion[idx].total ?
                      'bg-green-500' : 'bg-blue-500'
                    }`}
                    style={{ width: `${(categoryCompletion[idx].completed / categoryCompletion[idx].total) * 100}%` }}
                  ></div>
                </div>
              </div>
            ))}
            
            {/* Overall completion */}
            <div className="flex flex-col pt-2 border-t border-gray-200 mt-2">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center">
                  <span className="text-gray-700 font-medium">总体完成度</span>
                </div>
                <span className="text-sm text-gray-500">
                  {categoryCompletion.reduce((sum, cat) => sum + cat.completed, 0)} / {categoryCompletion.reduce((sum, cat) => sum + cat.total, 0)} 已完成
                </span>
              </div>
              <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden">
                <div 
                  className="bg-gradient-to-r from-blue-500 to-indigo-600 h-full transition-all duration-500 ease-out"
                  style={{ 
                    width: `${(categoryCompletion.reduce((sum, cat) => sum + cat.completed, 0) / 
                    categoryCompletion.reduce((sum, cat) => sum + cat.total, 0)) * 100}%` 
                  }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Display questions by categories */}
      {categories.map((category, categoryIndex) => (
        <div key={categoryIndex} className="mb-10">
          <div className="flex items-center mb-6">
            <div className="flex items-center bg-gradient-to-r from-blue-50 to-indigo-50 px-5 py-3 rounded-lg border border-blue-100">
              <span className="text-xl mr-3">{category.icon}</span>
              <span className="text-blue-700 font-semibold text-lg">{category.name}</span>
            </div>
            <div className="h-px bg-gray-200 flex-grow ml-6"></div>
            
            {/* Category completion badge */}
            <div className={`ml-4 px-3 py-1 rounded-full text-xs font-medium ${
              categoryCompletion[categoryIndex].completed === categoryCompletion[categoryIndex].total ? 
              'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
            }`}>
              {categoryCompletion[categoryIndex].completed === categoryCompletion[categoryIndex].total ? 
                '已完成' : `${categoryCompletion[categoryIndex].completed}/${categoryCompletion[categoryIndex].total}`
              }
            </div>
          </div>
          
          <div className="grid grid-cols-1 gap-6 mb-10">
            {categoryQuestions[categoryIndex].map((question, questionIndex) => {
              // Calculate the overall question index based on category
              let overallIndex = questionIndex;
              if (categoryIndex > 0) {
                // Add the number of questions in previous categories
                for (let i = 0; i < categoryIndex; i++) {
                  overallIndex += categoryQuestions[i].length;
                }
              }
              
              const isAnswered = isQuestionAnswered(question.value);
              
              return (
                <div 
                  key={questionIndex} 
                  className={`bg-gradient-to-r ${isAnswered ? 'from-gray-50 to-gray-100' : 'from-red-50 to-gray-50'} p-6 rounded-xl border ${isAnswered ? 'border-gray-200' : 'border-red-200'} hover:shadow-md transition-all duration-200 cursor-pointer group relative`}
                  onClick={() => onEditQuestion(overallIndex)}
                >
                  <div className="absolute top-5 right-5 text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                    </svg>
                  </div>
                  
                  <div className="flex items-center mb-4">
                    <div className={`h-8 w-8 rounded-full ${isAnswered ? 'bg-blue-100 text-blue-600' : 'bg-red-100 text-red-600'} flex items-center justify-center mr-4 flex-shrink-0 font-medium shadow-sm`}>
                      {questionIndex + 1}
                    </div>
                    <h3 className={`${isAnswered ? 'text-blue-600' : 'text-red-600'} font-medium text-lg`}>{question.label}</h3>
                    
                    {/* Answered/Unanswered badge */}
                    <div className={`ml-auto ${isAnswered ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'} text-xs px-2.5 py-0.5 rounded-full`}>
                      {isAnswered ? '已回答' : '未回答'}
                    </div>
                  </div>
                  
                  <div className="ml-12">
                    <p className="text-gray-500 mb-3">{decodeHtmlEntities(question.question)}</p>
                    <div className={`bg-white p-4 rounded-lg border ${isAnswered ? 'border-gray-200' : 'border-red-100'} shadow-sm`}>
                        {typeof question.value === 'string' ? (
                          <p className="text-gray-800 break-words leading-relaxed">{question.value || '未填写'}</p>
                        ) : (
                          formatMultiSelectionValue(question.value)
                        )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
      
      <div className="flex space-x-6 max-w-lg mx-auto">
        <button 
          onClick={onBack}
          disabled={isSubmitting}
          className="flex-1 flex items-center justify-center px-8 py-4 rounded-lg bg-gray-200 hover:bg-gray-300 transition-colors duration-300 text-gray-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronLeft size={20} className="mr-2" />
          返回编辑
        </button>
        
        <button 
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="flex-1 flex items-center justify-center px-8 py-4 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-400 hover:to-indigo-500 transition-all duration-300 text-white font-medium shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <Loader size={20} className="animate-spin mr-2" />
              提交中...
            </>
          ) : (
            <>
              提交问卷
              <Check size={20} className="ml-2" />
            </>
          )}
        </button>
      </div>
      
      {/* Incomplete Questions Modal */}
      {showIncompleteModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          {/* Semi-transparent backdrop */}
          <div 
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setShowIncompleteModal(false)}
          />
          
          {/* Modal content */}
          <div className="bg-white rounded-xl shadow-lg p-6 max-w-md m-4 relative z-10">
            <div className="flex items-start mb-4">
              <div className="bg-amber-100 p-3 rounded-full mr-4">
                <AlertTriangle className="h-6 w-6 text-amber-600" />
              </div>
              
              <div className="flex-1">
                <h3 className="text-lg font-medium text-gray-900 mb-2">未完成的问题</h3>
                <p className="text-gray-600 mb-4">
                  您还有 {missingQuestions.length} 个问题未回答。请先完成所有问题再提交问卷。
                </p>
                
                {/* List of incomplete questions */}
                <div className="mt-4 mb-4 max-h-60 overflow-y-auto rounded-lg border border-gray-200">
                  <ul className="divide-y divide-gray-200">
                    {missingQuestions.map((item, idx) => (
                      <li key={idx} className="p-3 hover:bg-gray-50">
                        <span className="text-sm text-gray-800 font-medium">{item.categoryName}</span>
                        <p className="text-sm text-gray-600 mt-1">{item.questionLabel}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              
              <button 
                onClick={() => setShowIncompleteModal(false)}
                className="text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="flex space-x-4">
              <button
                onClick={() => setShowIncompleteModal(false)}
                className="flex-1 py-2 px-4 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium rounded-lg transition-colors"
              >
                关闭
              </button>
              <button
                onClick={goToFirstIncompleteQuestion}
                className="flex-1 py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
              >
                前往未完成问题
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewPage;